import React from 'react';
import { DialogTitle, IconButton, Box } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';

/**
 * DialogTitleWithClose Component
 *
 * A reusable DialogTitle component with a close button in the top right corner.
 * This provides a consistent way for users to close dialogs without scrolling.
 *
 * @param {Object} props
 * @param {string} props.title - The dialog title text
 * @param {Function} props.onClose - Function to call when close button is clicked
 * @param {boolean} props.disableClose - If true, disables the close button
 * @param {Object} props.sx - Additional sx styles
 * @param {ReactNode} props.children - Optional children (will be used instead of title if provided)
 */
const DialogTitleWithClose = ({
  title,
  onClose,
  disableClose = false,
  sx = {},
  children,
}) => {
  return (
    <DialogTitle
      sx={{
        m: 0,
        p: 2,
        position: 'relative',
        ...sx,
      }}
    >
      <Box sx={{ pr: onClose && !disableClose ? 4 : 0 }}>
        {children || title}
      </Box>
      {onClose && (
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          disabled={disableClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
            '&:hover': {
              backgroundColor: theme => theme.palette.action.hover,
              color: theme => theme.palette.text.primary,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
    </DialogTitle>
  );
};

DialogTitleWithClose.propTypes = {
  title: PropTypes.string,
  onClose: PropTypes.func,
  disableClose: PropTypes.bool,
  sx: PropTypes.object,
  children: PropTypes.node,
};

export default DialogTitleWithClose;
